import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import SubmissionCard from "@/components/SubmissionCard";
import SubmissionDrawer from "@/components/SubmissionDrawer";
import UploadDialog from "@/components/UploadDialog";
import { Button } from "@/components/ui/button";
import { Plus, MegaphoneSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import StatsGrid from "@/components/StatsGrid";

export default function InfluencerDashboard() {
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resubmitFor, setResubmitFor] = useState(null);
  const [preselectCampaignId, setPreselectCampaignId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c, st] = await Promise.all([
        api.get("/submissions"),
        api.get("/campaigns"),
        api.get("/stats"),
      ]);
      setSubs(s.data);
      setCampaigns(c.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openUpload = () => { setResubmitFor(null); setPreselectCampaignId(null); setUploadOpen(true); };
  const openResubmit = (s) => { setResubmitFor(s); setPreselectCampaignId(null); setUploadOpen(true); };
  const openUploadFor = (campaignId) => { setResubmitFor(null); setPreselectCampaignId(campaignId); setUploadOpen(true); };

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-black text-4xl">My Submissions</h1>
          <p className="text-zinc-400 mt-1">Track content status and iterate on feedback.</p>
        </div>
        <Button
          onClick={openUpload}
          data-testid="new-submission-button"
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5 h-11"
        >
          <Plus size={18} weight="bold" className="mr-1" /> New submission
        </Button>
      </div>

      <StatsGrid stats={stats} role="influencer" />

      {campaigns.length > 0 && (
        <div className="mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading font-bold text-lg">Available campaigns</h2>
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-widest">{campaigns.length} open</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => openUploadFor(c.id)}
                data-testid={`available-campaign-${c.id}`}
                className="text-left rounded-xl border border-white/10 bg-[#0a0a0a] p-5 card-hover"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-base truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 font-mono truncate">by {c.brand_name}</div>
                  </div>
                  {c.is_open && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-[#FFD700]/25 bg-[#FFD700]/10 text-[#FFE44D] text-[10px] font-mono uppercase tracking-wide">
                      Open
                    </span>
                  )}
                </div>
                {c.description && (
                  <p className="text-sm text-zinc-400 line-clamp-2">{c.description}</p>
                )}
                <div className="mt-3 text-xs text-[#FFD700] font-medium">Submit content →</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-heading font-bold text-lg mb-4">All submissions</h2>
        {subs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-12 text-center">
            <MegaphoneSimple size={40} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
            <p className="text-zinc-400">No submissions yet. Upload your first piece of content.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subs.map((s) => (
              <SubmissionCard
                key={s.id}
                submission={s}
                showResubmit
                onClick={() => setActiveId(s.id)}
                onResubmit={openResubmit}
              />
            ))}
          </div>
        )}
      </div>

      <SubmissionDrawer
        submissionId={activeId}
        open={!!activeId}
        onClose={() => setActiveId(null)}
        role="influencer"
        onUpdated={load}
      />
      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        campaigns={campaigns}
        onCreated={load}
        resubmitFor={resubmitFor}
      />
    </div>
  );
}
